import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Sparkles, RotateCw } from "lucide-react";
import VoiceMicButton from "@/components/VoiceMicButton";
import SavedPromptsPicker from "@/components/SavedPromptsPicker";
import { useSavedPrompts } from "@/hooks/useSavedPrompts";

/**
 * Modal that lets a user pick slides from a completed deck, give per-
 * slide refinement instructions, and fork a NEW deck.
 *
 * Layout: each slide renders a LARGE thumbnail (large enough to read
 * the slide content) with a checkbox + numeric label above it. When the
 * checkbox is ticked, a full-width instructions textarea slides in
 * below the thumbnail. The optional title field was removed because
 * users prefer to express layout/content changes purely through natural-
 * language instructions — Opus picks up title intent from the prose.
 *
 * Backend contract: `POST /api/decks/{src_deck_id}/regenerate-slides`
 *
 *   body: { slides: [{slide_id, instructions}] }
 *
 * The endpoint refuses if literally every slide is selected (the user
 * should hit the regular "Regenerate deck" button for that). Reused
 * slides are free; the user pays only for the regenerated count.
 *
 * Props:
 *   open: bool — controlled visibility
 *   onClose: () => void
 *   deck: the source deck doc (must have `id`, `slide_ids`,
 *         `slide_thumbnails` and `status === 'complete'`)
 *   slideRows: per-slide presentation rows already fetched by the
 *              parent (used for titles + thumbnails). Optional; if not
 *              provided we render index-based labels.
 */
export default function RegenerateSlidesModal({ open, onClose, deck, slideRows = [] }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState({});        // { slide_id: true }
  // { slide_id: { instructions } } — the per-slide edit payload.
  // Stored separately from `selected` so toggling the checkbox doesn't
  // wipe in-progress text the user already typed.
  const [edits, setEdits] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // One textarea ref per slide id — used to splice voice-transcribed
  // text into the right slide's instructions field.
  const instructionRefs = useRef({});
  // Saved-prompts library — shared with PPT Studio + Personalize tab.
  const { prompts: savedPrompts, refresh: refreshPrompts } = useSavedPrompts();

  if (!deck) return null;

  const slideIds = deck.slide_ids || [];
  const slidesById = Object.fromEntries((slideRows || []).map(s => [s.id, s]));

  const totalSelected = Object.values(selected).filter(Boolean).length;
  const totalSlides = slideIds.length;
  const reusedCount = totalSlides - totalSelected;

  // Per-slide validity: each selected slide must have non-empty
  // instructions. Empty selections are a no-op (the user pays for a
  // regen they didn't actually instruct), so we disable Submit until
  // every selected slide has something. The submit-time guard remains
  // as a belt-and-braces toast in case state races us.
  const selectedIds = Object.keys(selected).filter(id => selected[id]);
  const slidesNeedingInput = selectedIds.filter(id => !(edits[id]?.instructions || "").trim());
  const allSelectedHaveInput = totalSelected > 0 && slidesNeedingInput.length === 0;

  // UX guard: the backend rejects "select everything" because that's
  // really a full regenerate.
  const wouldRegenerateAll = totalSelected > 0 && totalSelected === totalSlides;

  const toggleSlide = (id) => {
    setSelected(prev => ({ ...prev, [id]: !prev[id] }));
    setEdits(prev => prev[id] ? prev : { ...prev, [id]: { instructions: "" } });
  };

  const setInstructions = (id, value) => {
    setEdits(prev => ({
      ...prev,
      [id]: { ...(prev[id] || {}), instructions: value },
    }));
  };

  const handleSubmit = async () => {
    const ids = Object.keys(selected).filter(id => selected[id]);
    if (ids.length === 0) {
      toast.error("Select at least one slide to regenerate");
      return;
    }
    const empty = ids.filter(id => !(edits[id]?.instructions || "").trim());
    if (empty.length) {
      toast.error(`Add refinement instructions for ${empty.length} selected slide(s)`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/decks/${deck.id}/regenerate-slides`,
        {
          slides: ids.map(id => ({
            slide_id: id,
            instructions: (edits[id]?.instructions || "").trim(),
          })),
        },
        { withCredentials: true },
      );
      toast.success(
        `Forked deck — regenerating ${res.data.regen_count}, reusing ${res.data.reused_count}`,
      );
      onClose?.();
      navigate("/history");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Failed to fork deck";
      if (status === 402) {
        toast.error(detail);
        window.dispatchEvent(new CustomEvent("credits:open-upgrade"));
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // When the modal closes we reset state so the next open starts fresh.
  // Without this, reopening shows stale checkboxes/text from the
  // previous session — confusing and accidentally bills the user.
  const handleOpenChange = (next) => {
    if (!next) {
      setSelected({});
      setEdits({});
      instructionRefs.current = {};
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid="regen-slides-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-4 h-4 text-amber-500" />
            Regenerate specific slides
          </DialogTitle>
          <DialogDescription className="text-xs">
            Pick the slides you want to redo and describe how. Unchanged slides
            are reused as-is (no extra cost). The result is saved as a new deck
            so the original stays intact.
          </DialogDescription>
        </DialogHeader>

        {/* Selection summary strip */}
        <div className="flex items-center justify-between rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-xs" data-testid="regen-summary">
          <span>
            <span className="font-semibold text-gray-900">{totalSelected}</span>
            {" "}to regenerate, {" "}
            <span className="font-semibold text-gray-900">{reusedCount}</span>
            {" "}reused
          </span>
          {wouldRegenerateAll ? (
            <span className="text-amber-700 font-medium" data-testid="regen-warning">
              You picked all slides — use the regular Regenerate deck button instead.
            </span>
          ) : slidesNeedingInput.length > 0 ? (
            <span className="text-gray-500" data-testid="regen-missing-input-hint">
              Add instructions for {slidesNeedingInput.length} selected slide(s) to continue.
            </span>
          ) : null}
        </div>

        <div className="space-y-4" data-testid="regen-slide-list">
          {slideIds.map((sid, idx) => {
            const isSel = !!selected[sid];
            const row = slidesById[sid];
            const fallbackTitle =
              row?.inputs?.title ||
              row?.content?.split("\n")[0]?.slice(0, 80) ||
              `Slide ${idx + 1}`;
            return (
              <div
                key={sid}
                className={`rounded-lg border transition-all overflow-hidden ${isSel ? "border-amber-300 ring-1 ring-amber-200" : "border-gray-200"}`}
                data-testid={`regen-slide-row-${idx}`}
              >
                {/* Header strip — checkbox + slide number + title. The
                    whole strip is clickable so users don't have to aim
                    for the small checkbox target. */}
                <label className="flex items-center gap-3 px-4 py-2.5 cursor-pointer bg-white hover:bg-gray-50 border-b border-gray-100">
                  <Checkbox
                    checked={isSel}
                    onCheckedChange={() => toggleSlide(sid)}
                    data-testid={`regen-checkbox-${idx}`}
                  />
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide tabular-nums">
                    Slide {idx + 1}
                  </span>
                  <span
                    className="text-sm text-gray-800 truncate flex-1"
                    title={fallbackTitle}
                  >
                    {fallbackTitle}
                  </span>
                </label>

                {/* Thumbnail — large enough to actually READ the slide
                    content. Centered on a soft grey backdrop so the
                    slide art has room to breathe. Aspect-video matches
                    PPTX 16:9. We don't lazy-load because there are at
                    most 20 thumbs in a deck — eager fetch is faster
                    perceived UX than the placeholder flash. */}
                <div className={`px-4 py-3 ${isSel ? "bg-amber-50/40" : "bg-gray-50/60"}`}>
                  <div className="mx-auto w-full max-w-2xl aspect-video bg-white rounded-md overflow-hidden border border-gray-200 shadow-sm">
                    <img
                      src={`${API}/thumbnails/${sid}`}
                      alt={`Slide ${idx + 1}`}
                      className="w-full h-full object-contain"
                      onError={(e) => { e.target.style.opacity = "0.3"; }}
                    />
                  </div>
                </div>

                {isSel && (
                  <div className="px-4 pb-4 pt-2 space-y-1.5 bg-amber-50/40 border-t border-amber-200/70" data-testid={`regen-edits-${idx}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                        Refinement instructions
                      </p>
                      <SavedPromptsPicker
                        value={edits[sid]?.instructions || ""}
                        onChange={(v) => setInstructions(sid, v)}
                        testId={`regen-instructions-${idx}`}
                        prompts={savedPrompts}
                        onPromptsChange={refreshPrompts}
                      />
                    </div>
                    {/* Textarea + floating mic, same pattern as PPT
                        Studio. The bookmark/library icons stay in the
                        header strip; only the mic overlays the textarea
                        so it stays a one-tap action while typing. */}
                    <div className="relative">
                      <Textarea
                        ref={(el) => { instructionRefs.current[sid] = el; }}
                        value={edits[sid]?.instructions || ""}
                        onChange={e => setInstructions(sid, e.target.value)}
                        placeholder="e.g., Replace the bullet list with a 2x2 matrix highlighting the 35% growth, and use the brand teal for the headline."
                        className="min-h-[90px] border-gray-200 text-sm bg-white pr-12 pb-12"
                        data-testid={`regen-instructions-input-${idx}`}
                      />
                      <div className="absolute bottom-2 right-2">
                        <VoiceMicButton
                          value={edits[sid]?.instructions || ""}
                          onChange={(v) => setInstructions(sid, v)}
                          inputRef={{ current: instructionRefs.current[sid] }}
                          testId={`regen-instructions-${idx}`}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting} data-testid="regen-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || totalSelected === 0 || wouldRegenerateAll || !allSelectedHaveInput}
            className="bg-black hover:bg-gray-800"
            data-testid="regen-submit-btn"
          >
            {submitting ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Forking…</>
            ) : (
              <><RotateCw className="w-3.5 h-3.5 mr-1.5" />Regenerate {totalSelected || ""} slide{totalSelected === 1 ? "" : "s"}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
